
import { hideModal } from "@/core/helpers/dom";
import { ElNotification } from "element-plus";
import { defineComponent, PropType } from "vue";
import * as Yup from "yup";

interface EmailContext {
  targetType: string;
  targetId: string;
}

export default defineComponent({
  name: "SendEmailModal",
  emits: ["send-email"],
  props: {
    context: {
      type: Object as PropType<EmailContext>,
      default: () => ({ targetType: "", targetId: "" }),
    },
  },
  data() {
    return {
      asunto: "" as string,
      mensaje: "" as string,
      sending: false as boolean,
      currentTarget: { targetType: "", targetId: "" } as EmailContext,
      schema: null as any,
    };
  },
  created() {
    // esquema Yup para validación local
    this.schema = Yup.object({
      asunto: Yup.string().required(
        this.$t("modal.sendEmail.asuntoRequerido") || "Asunto requerido"
      ),
      mensaje: Yup.string().required(
        this.$t("modal.sendEmail.mensajeRequerido") || "Mensaje requerido"
      ),
    });
  },
  watch: {
    // cuando el padre prepara context antes de abrir la modal, lo sincronizamos
    context: {
      handler(newVal: EmailContext) {
        if (newVal && (newVal.targetType || newVal.targetId)) {
          this.currentTarget = { ...newVal };
          // resetear campos cuando se abre la modal
          this.asunto = "";
          this.mensaje = "";
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    isFormValid(): boolean {
      return this.asunto.trim().length > 0 && this.mensaje.trim().length > 0;
    },
    onCancel(): void {
      const el = this.$refs.modalRef as HTMLElement | undefined;
      if (el) {
        try {
          hideModal(el);
        } catch {
          if ((window as any).bootstrap?.Modal) {
            const inst = (window as any).bootstrap.Modal.getInstance(el);
            inst?.hide();
          }
        }
      }
    },
    async onSend(): Promise<void> {
      // validación con Yup
      try {
        await this.schema.validate(
          {
            asunto: this.asunto,
            mensaje: this.mensaje,
          },
          { abortEarly: false }
        );
      } catch (err: any) {
        // mostrar primeros errores
        const message =
          err?.errors?.[0] ||
          this.$t("modal.sendEmail.validacionError") ||
          "Corrige los campos";
        ElNotification({ message, type: "warning" });
        return;
      }

      this.sending = true;
      try {
        // emitir al padre con los datos completos
        this.$emit("send-email", {
          asunto: this.asunto,
          mensaje: this.mensaje,
          targetType: this.currentTarget.targetType,
          targetId: this.currentTarget.targetId,
        });
        // cerrar modal
        const el = this.$refs.modalRef as HTMLElement | undefined;
        if (el) {
          try {
            hideModal(el);
          } catch {
            if ((window as any).bootstrap?.Modal) {
              const inst = (window as any).bootstrap.Modal.getInstance(el);
              inst?.hide();
            }
          }
        }
      } catch (err) {
        console.error(err);
        ElNotification({
          message:
            this.$t("modal.sendEmail.error") || "Error preparando el envío",
          type: "error",
        });
      } finally {
        this.sending = false;
      }
    },
  },
});
